import type { Action } from './actions'
import type { LoginForm, CustomerAnalyticsData } from 'shared/types/api'
import type { CookiesConsents } from './types'

export type State = {
    cookiesConsentsDone: ?boolean,
    manageCookies?: ?boolean,
    cookiesConsents: CookiesConsents,
    trackingStatus: string,
    token: ?string,
    authenticated: ?boolean,
    loginType?: 'FACEBOOK' | 'GOOGLE' | 'CLASSIC' | 'APPLE',
    onSocialPending: ?(boolean) => void,
    subscriptionSuccess: boolean,
    trySubscription: boolean,
    userData: ?LoginForm,
    analyticsData: ?CustomerAnalyticsData,
    touchID: boolean,
    signinDone: boolean,
    appBannerClosed: ?boolean,
    notificationsDisabled?: boolean,
    notificationsConsentDate?: Date,
    toast: {
        type: ?string,
        data: ?{
            points?: number
        }
    },
    numberOfPagesVisited: number,
    error: any,
    page: ?string,
    answeredGeofenceAccess: boolean,
    geofenceAllowed: boolean,
    optinPush?: boolean
}

const initialState: State = {
    cookiesConsentsDone: undefined,
    cookiesConsents: {
        media: false,
        technical: true,
        tracking: false
    },
    trackingStatus: 'not-determined',
    notificationsConsentDate: undefined,
    numberOfPagesVisited: 0,
    authenticated: undefined,
    onSocialPending: undefined,
    token: undefined,
    subscriptionSuccess: false,
    trySubscription: false,
    touchID: false,
    signinDone: false,
    userData: undefined,
    analyticsData: undefined,
    appBannerClosed: false,
    error: null,
    toast: {
        type: null,
        data: null
    },
    page: null,
    answeredGeofenceAccess: false,
    geofenceAllowed: false,
    optinPush: true
}

export const reducer = (state: State = initialState, action: Action): State => {
    switch (action.type) {
        case 'NEWSLETTER_SUBSCRIPTION_SUCCESS':
            return {
                ...state,
                subscriptionSuccess: true,
                trySubscription: true
            }
        case 'NEWSLETTER_SUBSCRIPTION_ERROR':
            return {
                ...state,
                subscriptionSuccess: false,
                trySubscription: true
            }
        case 'CONSENT_ALL_COOKIES':
            return {
                ...state,
                cookiesConsentsDone: true,
                manageCookies: undefined,
                cookiesConsents: {
                    media: true,
                    technical: true,
                    tracking: true
                }
            }
        case 'DECLINE_ALL_COOKIES':
            return {
                ...state,
                cookiesConsentsDone: true,
                manageCookies: undefined
            }
        case 'MANAGE_COOKIES':
            return {
                ...state,
                manageCookies: action.payload.show
            }
        case 'CONSENT_COOKIES':
            const cookiesConsents = action.payload.consents
            return {
                ...state,
                cookiesConsentsDone: true,
                manageCookies: undefined,
                cookiesConsents
            }
        case 'TRACKING_STATUS':
            return {
                ...state,
                cookiesConsentsDone: initialState.cookiesConsentsDone,
                cookiesConsents: initialState.cookiesConsents,
                trackingStatus: action.trackingStatus
            }
        case 'SIGNUP_SEND_FORM_CONNECTED':
            return {
                ...state,
                token: action.token,
                authenticated: true,
                userData: undefined,
                signinDone: true
            }
        case 'SIGNIN_SEND_FORM_SUCCESS':
            return {
                ...state,
                token: action.token,
                authenticated: true,
                // $FlowFixMe
                userData: {},
                loginType: action.loginType
            }
        case 'SIGNIN_WITH_TOUCH_ID':
            return {
                ...state,
                token: action.token,
                authenticated: true,
                userData: undefined
            }
        case 'SIGNIN_DONE':
            return {
                ...state,
                signinDone: true
            }
        case 'SOCIAL_PENDING':
            return {
                ...state,
                ...action.data
            }
        case 'LOGOUT_DONE':
            return {
                ...state,
                token: undefined,
                authenticated: undefined,
                userData: undefined,
                analyticsData: undefined,
                signinDone: false
            }
        case 'SIGNUP_RESET':
        case 'PROFILE_DELETE_SUCCESS':
            return {
                ...state,
                token: undefined,
                authenticated: undefined,
                userData: undefined,
                analyticsData: undefined,
                touchID: false
            }
        case 'PAGE_API_AUTH_ERROR':
            return {
                ...state,
                token: undefined,
                authenticated: undefined,
                userData: undefined,
                analyticsData: undefined,
                error: action.error,
                page: action.page
            }
        case 'UPDATE_TOKEN':
            return {
                ...state,
                token: state.token ? action.token : undefined
            }
        case 'CLOSE_INSERT':
            return {
                ...state,
                appBannerClosed: true
            }
        case 'TOGGLE_NOTIFICATIONS_ENABLED':
            const today = new Date()
            return {
                ...state,
                notificationsDisabled: action.notificationsDisabled,
                notificationsConsentDate: today
            }
        case 'RESET_TOAST':
            return {
                ...state,
                toast: initialState.toast
            }
        case 'TOGGLE_TOUCH_ID':
            return {
                ...state,
                touchID: !state.touchID
            }
        case 'PAGE_DATA_PROFILE_RESPONSE':
        case 'PAGE_DATA_HOMEPAGE_RESPONSE':
        case 'PAGE_DATA_ROOT_RESPONSE':
            return {
                ...state,
                analyticsData: action.data.analyticsData
            }
        case 'USER_ANALYTICS_DATA':
            return {
                ...state,
                analyticsData: action.analyticsData
            }
        //$FlowFixMe
        case '@@router/LOCATION_CHANGE':
            const value = action.payload.action === 'PUSH' ? 1 : 0
            return {
                ...state,
                numberOfPagesVisited: state.numberOfPagesVisited + value
            }
        case 'LOGOUT_AFTER_ERROR':
            return {
                ...initialState
            }
        case 'PREVENT_PERSISTED_ERROR':
            return {
                ...state,
                error: null
            }
        case 'ALLOW_GEOFENCE':
            return {
                ...state,
                geofenceAllowed: true,
                answeredGeofenceAccess: true
            }
        case 'DENY_GEOFENCE':
            return {
                ...state,
                geofenceAllowed: false,
                answeredGeofenceAccess: true
            }
        case 'UPDATE_OPTIN_PUSH':
            return {
                ...state,
                optinPush: action.optinPush
            }
        default:
            return {
                ...state,
                subscriptionSuccess: false,
                trySubscription: false
            }
    }
}
